/**
 * Excel export for a drill-down: the SAME columns the table shows, over every
 * row of the filtered population (the hook walks all pages before calling
 * this — never just the page on screen).
 */
import * as XLSX from 'xlsx';
import { DrilldownColumn, exportValue, isFrozenRow } from './columns';

/**
 * CR-7270: a spreadsheet outlives the tab it came from, so an export of a
 * frozen month carries the boundary its values are AS AT as a column — the
 * same 'As At' the server-side exports emit — and a live export is the file
 * it always was. Present only when at least one row is frozen.
 */
function asAtColumn<T>(rows: T[], formatDate?: (d: string | Date) => string): DrilldownColumn<T>[] {
  if (!rows.some(isFrozenRow)) return [];
  return [{
    key: 'frozen_as_at', label: 'As At',
    value: (row: T) => {
      const v = (row as { frozen_as_at?: string | null }).frozen_as_at;
      return v ? (formatDate ? formatDate(v) : v) : null;
    },
  } as DrilldownColumn<T>];
}

function slug(text: string): string {
  return text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

interface PageOf<T> {
  count: number;
  results: T[];
}

/**
 * Every row of a paginated list: page 1 gives the count, the remaining pages
 * are fetched `concurrency` at a time and re-assembled in page order.
 *
 * Customer review 11 Sep (items 5a / 6d): the drill-down export must be
 * the FULL population, and a large supplier runs to 55 pages of 1,000 GRNs —
 * about 90s one page at a time, about 25s four at a time.
 *
 * @param fetchPage - Fetches one 1-based page of the population.
 * @param pageSize - The page size `fetchPage` requests (to derive the page count).
 * @param concurrency - Pages in flight at once.
 * @returns All rows, in list order.
 */
export async function fetchEveryPage<T>(
  fetchPage: (page: number) => Promise<PageOf<T>>,
  pageSize: number,
  concurrency = 4,
): Promise<T[]> {
  const first = await fetchPage(1);
  const pages = Math.ceil((first.count || 0) / pageSize);
  const rest: T[][] = [];
  for (let start = 2; start <= pages; start += concurrency) {
    const batch: Promise<PageOf<T>>[] = [];
    for (let page = start; page < start + concurrency && page <= pages; page += 1) {
      batch.push(fetchPage(page));
    }
    const results = await Promise.all(batch);
    results.forEach((result) => rest.push(result.results || []));
  }
  return [...(first.results || []), ...rest.flat()];
}

/** `supplier-performance-<supplier>-<metric>.xlsx` */
export function drilldownFileName(supplierName: string, metric: string): string {
  return `supplier-performance-${slug(supplierName)}-${slug(metric)}.xlsx`;
}

export function exportDrilldownRows<T>(
  rows: T[],
  columns: DrilldownColumn<T>[],
  fileName: string,
  sheetName: string,
  /** The user's date format, so the As At cell reads like the sibling date columns. */
  formatDate?: (d: string | Date) => string,
): void {
  const allColumns = [...columns, ...asAtColumn(rows, formatDate)];
  const headers = allColumns.map((column) => column.label);
  const data = rows.map((row) =>
    allColumns.reduce<Record<string, string | number>>((cells, column) => {
      cells[column.label] = exportValue(column, row);
      return cells;
    }, {}),
  );
  const worksheet = XLSX.utils.json_to_sheet(data, { header: headers });
  const workbook = XLSX.utils.book_new();
  // Excel caps sheet names at 31 characters.
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName.slice(0, 31));
  XLSX.writeFile(workbook, fileName);
}
